import React from 'react';
import { View, StyleSheet, StyleProp, ViewStyle, Platform } from 'react-native';
import { Text } from '@/components/common/text';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { useRadius } from '@/hooks/shared/useRadius';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { Hoverable } from '@/components/shared/redesign/Hoverable';

export type CardBrand = 'visa' | 'mastercard' | 'amex' | 'discover' | 'diners' | 'jcb' | 'maestro' | 'unionpay' | 'bank' | 'unknown';

interface PaymentMethodCardProps {
  brand: CardBrand;
  label: string;
  lastFour: string;
  isDefault?: boolean;
  isExpired?: boolean;
  interactive?: boolean;
  onSetAsDefault?: () => void;
  onDelete?: () => void;
  isUpdating?: boolean;
  canDelete?: boolean;
  cardholderName?: string;
  style?: StyleProp<ViewStyle>;
}

const CARD_ICON_MAP: Record<string, any> = {
  visa: RESIDENTS_SVG_COMPONENTS.cards?.VISA,
  mastercard: RESIDENTS_SVG_COMPONENTS.cards?.MASTERCARD,
  amex: RESIDENTS_SVG_COMPONENTS.cards?.AMEX,
  discover: RESIDENTS_SVG_COMPONENTS.cards?.DISCOVER,
  diners: RESIDENTS_SVG_COMPONENTS.cards?.DINERS,
  jcb: RESIDENTS_SVG_COMPONENTS.cards?.JCB,
  maestro: RESIDENTS_SVG_COMPONENTS.cards?.MAESTRO,
  unionpay: RESIDENTS_SVG_COMPONENTS.cards?.UNIONPAY,
};

export const PaymentMethodCard: React.FC<PaymentMethodCardProps> = React.memo(({
  brand,
  label,
  lastFour,
  isDefault = false,
  isExpired = false,
  interactive = false,
  onSetAsDefault,
  onDelete,
  isUpdating = false,
  canDelete = true,
  cardholderName,
  style,
}) => {
  const { colors, semantic, mode } = useThemeColors();
  const radius = useRadius();
  const space = useSpacing();
  // "Remove" takes the row tint: a padded box pulled back by the same amounts,
  // so the tint has room around the word while the word itself stays put.
  const removeBox = {
    paddingHorizontal: space.sm,
    marginHorizontal: -space.sm,
    paddingVertical: space.xs,
    marginVertical: -space.xs,
    borderRadius: radius.sm,
  };

  const CardIcon = CARD_ICON_MAP[brand];
  const BankIcon = RESIDENTS_SVG_COMPONENTS.BANK;
  const CreditCardFallback = RESIDENTS_SVG_COMPONENTS.CREDIT_CARD;

  // Scoped premium surface using the app's blue accent — intentionally distinct from
  // colors.card.bg so GlassCard and all other shared surfaces are unaffected.
  const elevatedBg = mode === 'light' ? '#FFFFFF' : 'rgba(70,114,254,0.08)';
  const elevatedBorder = mode === 'light' ? 'rgba(70,114,254,0.18)' : 'rgba(70,114,254,0.22)';

  const cardBase = {
    backgroundColor: elevatedBg,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: elevatedBorder,
    ...Platform.select({
      web: {
        boxShadow: mode === 'light'
          ? '0 2px 16px rgba(70,114,254,0.10)'
          : '0 2px 20px rgba(70,114,254,0.15)',
      },
      android: {
        overflow: 'hidden' as const,
      },
      default: {
        // overflow:hidden omitted on iOS so the drop shadow renders outside the view.
        shadowColor: '#4672FE',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: mode === 'light' ? 0.12 : 0.20,
        shadowRadius: 12,
        elevation: 4,
      },
    }),
  };

  const renderIcon = () => {
    if (brand === 'bank' && BankIcon) {
      return <BankIcon width={22} height={16} color={colors.text.tertiary} />;
    }
    if (CardIcon) {
      return <CardIcon width={22} height={16} />;
    }
    if (CreditCardFallback) {
      return <CreditCardFallback width={22} height={16} color={colors.text.tertiary} />;
    }
    return null;
  };

  const renderBadge = () => {
    if (isExpired) {
      return (
        <View style={[styles.expiredBadge, { backgroundColor: colors.accent.redBg }]}>
          <Text style={[styles.expiredBadgeText, { color: colors.accent.red }]}>EXPIRED</Text>
        </View>
      );
    }
    if (isDefault) {
      return (
        <View style={[styles.defaultBadge, { backgroundColor: colors.accent.greenBg }]}>
          <Text style={[styles.defaultBadgeText, { color: semantic.interactive.primary }]}>DEFAULT</Text>
        </View>
      );
    }
    return null;
  };

  const showActionRow = interactive && (onSetAsDefault || onDelete);
  const canSetDefault = !isDefault && !isExpired && onSetAsDefault;

  return (
    <View
      style={[
        interactive
          ? [cardBase, styles.cardInteractive]
          : [cardBase, styles.card],
        isDefault && !isExpired && styles.cardDefault,
        isExpired && styles.cardExpired,
        style,
      ]}
    >
      <View style={[styles.cardContent, !showActionRow && styles.cardContentStandalone]}>
        <View style={styles.topRow}>
          <View style={[styles.iconContainer, {
            backgroundColor: mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.07)',
            borderColor: mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)',
          }]}>
            {renderIcon()}
          </View>
          {renderBadge()}
        </View>

        <View style={styles.bottomRow}>
          <Text style={[styles.label, { color: colors.text.tertiary }]} numberOfLines={2}>{label}</Text>
          {cardholderName ? (
            <Text style={[styles.cardholderName, { color: colors.text.muted }]} numberOfLines={2}>{cardholderName}</Text>
          ) : null}
          <Text
            style={[styles.lastFour, { color: colors.text.dim }, isExpired && { textDecorationLine: 'line-through', color: colors.text.dimmer }]}
            numberOfLines={1}
          >
            {`\u2022\u2022\u2022\u2022 ${lastFour}`}
          </Text>
        </View>
      </View>

      {showActionRow && (
        <>
          <View style={[styles.actionSeparator, { backgroundColor: colors.separator }]} />
          <View style={styles.actionRow}>
            {canSetDefault ? (
              <Hoverable
                hover="link"
                onPress={onSetAsDefault}
                disabled={isUpdating}
                style={styles.actionTouchable}
              >
                {({ textStyle }) => (
                  <Text style={[styles.setDefaultText, { color: semantic.interactive.primary }, isUpdating && styles.textDisabled, textStyle]}>
                    Make default
                  </Text>
                )}
              </Hoverable>
            ) : (
              <View />
            )}
            {onDelete && canDelete && (
              <Hoverable hover="row" onPress={onDelete} disabled={isUpdating} style={removeBox}>
                <Text style={[styles.removeText, { color: colors.text.muted }, isUpdating && styles.textDisabled]}>
                  Remove
                </Text>
              </Hoverable>
            )}
          </View>
        </>
      )}
    </View>
  );
});

PaymentMethodCard.displayName = 'PaymentMethodCard';

const styles = StyleSheet.create({
  card: {
    width: 160,
    padding: 16,
    justifyContent: 'space-between',
    minHeight: 110,
  },
  cardInteractive: {
    flex: 1,
    minWidth: 140,
  },
  cardDefault: {
    borderColor: 'rgba(70,114,254,0.35)',
  },
  cardExpired: {
    borderColor: 'rgba(245,85,96,0.25)',
  },
  cardContent: {
    flex: 1,
    justifyContent: 'space-between',
    padding: 20,
  },
  cardContentStandalone: {
    padding: 0,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  iconContainer: {
    width: 44,
    height: 30,
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  defaultBadge: {
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  defaultBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  expiredBadge: {
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  expiredBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  bottomRow: {
    gap: 3,
    marginTop: 16,
  },
  label: {
    ...REDESIGN_TYPOGRAPHY.small,
    fontWeight: '600',
  },
  cardholderName: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  lastFour: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  actionSeparator: {
    height: 1,
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  actionTouchable: {
    paddingVertical: 2,
  },
  setDefaultText: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
  },
  removeText: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  textDisabled: {
    opacity: 0.4,
  },
});
